import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Map as LeafletMap,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { STATUS_META } from '../../../libs/stopStatus';
import { parseUtcDateTime, formatLocalHHMM } from '../../../utils/datetime';
import { TILE_URL, TILE_ATTRIBUTION } from '../../../utils/leaflet';
import useLeafletAutoInvalidate from '../../../hooks/useLeafletAutoInvalidate';
import { getStopRowKey } from './utils';

// Read-only map view for a route card (SCT-692 / mock 010). Visual style
// follows Live Route Monitoring — same basemap (both draw the shared
// utils/leaflet tiles) and numbered divIcon markers — but the rendering is
// independent: no components or styles are imported from MonitoringTab.
// Route-ops stops carry no coordinates in locationRoutes; the parent joins them
// on before passing.

const MARKER_PX = Number.parseInt(theme.sizes.mapMarkerSize, 10);

const StyledDiv = styledComponent('div', {
  // Same muted basemap as the Monitoring maps (see theme.filters).
  '& .leaflet-tile-pane': {
    filter: theme.filters.mutedBasemap,
  },
  '& .rsm-missing-note': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.labelGap,
  },
  '& .rsm-empty': {
    padding: theme.spacing.emptyStatePadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.inner,
  },
  '& .rsm-marker-icon': {
    background: 'transparent',
    border: 'none',
  },
  '& .rsm-tip-name': {
    fontWeight: theme.fontWeights.semibold,
  },
});

// Status tone for a stop — same source the list rows' StopStatusPill uses,
// so marker colors always match the pills.
export function stopToneFor(stop) {
  const statusId = stop && stop.derivedStatus && stop.derivedStatus.status;
  const meta = statusId ? STATUS_META[statusId] : null;
  return meta ? meta.tone : 'neutral';
}

export function makeStopMarkerIcon(number, tone) {
  const colors = theme.statusPillTones[tone] || theme.statusPillTones.neutral;
  return L.divIcon({
    className: 'rsm-marker-icon',
    html:
      `<span style="display:flex;align-items:center;justify-content:center;` +
      `width:${theme.sizes.mapMarkerSize};height:${theme.sizes.mapMarkerSize};` +
      `border-radius:50%;background:${colors.softBg};color:${theme.colors.white};` +
      `font-weight:${theme.fontWeights.semibold};font-size:${theme.sizes.smallFontSize};` +
      `border:2px solid ${theme.colors.white};box-shadow:${theme.shadows.card};">` +
      `${number}</span>`,
    iconSize: [MARKER_PX, MARKER_PX],
    iconAnchor: [MARKER_PX / 2, MARKER_PX / 2],
  });
}

function formatCalcTime(value) {
  const parsed = parseUtcDateTime(value);
  return parsed ? formatLocalHHMM(parsed) : '—';
}

function RouteStopsMap({ stops, height }) {
  const mapRef = useRef(null);

  const plotted = useMemo(
    () =>
      (stops || []).filter(
        (s) =>
          s.coords &&
          typeof s.coords.lat === 'number' &&
          typeof s.coords.long === 'number',
      ),
    [stops],
  );
  const missingCount = (stops || []).length - plotted.length;

  const positions = useMemo(
    () => plotted.map((s) => [s.coords.lat, s.coords.long]),
    [plotted],
  );
  const bounds = useMemo(
    () => (positions.length > 0 ? L.latLngBounds(positions) : null),
    [positions],
  );

  // The card body this map sits in settles its size after mount, so the tile
  // layer needs a nudge once layout is stable — re-run on `bounds` because a
  // new stop set can change the box before the observer sees it.
  useLeafletAutoInvalidate(mapRef, [bounds]);

  if (plotted.length === 0) {
    return (
      <StyledDiv>
        <div className="rsm-empty" data-testid="rsm-empty">
          No stops with coordinates to map.
        </div>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      {missingCount > 0 && (
        <div className="rsm-missing-note" data-testid="rsm-missing-note">
          {missingCount} stop{missingCount === 1 ? '' : 's'} not shown (no
          coordinates).
        </div>
      )}
      <div data-testid="rsm-map">
        <LeafletMap
          ref={mapRef}
          bounds={bounds}
          boundsOptions={{ padding: [40, 40], maxZoom: 15 }}
          style={{ height, width: '100%' }}
          minZoom={3}
          maxZoom={18}
          scrollWheelZoom={false}
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
          <Polyline
            positions={positions}
            color={theme.colors.accent}
            weight={4}
            opacity={0.9}
          />
          {plotted.map((s, idx) => {
            const hasWindow = Boolean(
              s.startTimeWindowDisplay || s.endTimeWindowDisplay,
            );
            return (
              <Marker
                key={getStopRowKey(s)}
                position={[s.coords.lat, s.coords.long]}
                icon={makeStopMarkerIcon(idx + 1, stopToneFor(s))}
              >
                <Tooltip direction="top" offset={[0, -(MARKER_PX / 2)]}>
                  <div>
                    <div className="rsm-tip-name">{s.displayName || s.id}</div>
                    <div>
                      Arrival–Depart: {formatCalcTime(s.calculatedStartTime)}–
                      {formatCalcTime(s.calculatedEndTime)}
                    </div>
                    {hasWindow && (
                      <div>
                        Window: {s.startTimeWindowDisplay}–
                        {s.endTimeWindowDisplay}
                      </div>
                    )}
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </LeafletMap>
      </div>
    </StyledDiv>
  );
}

RouteStopsMap.propTypes = {
  stops: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.string,
      coords: PropTypes.shape({
        lat: PropTypes.number,
        long: PropTypes.number,
      }),
      derivedStatus: PropTypes.shape({ status: PropTypes.string }),
      calculatedStartTime: PropTypes.string,
      calculatedEndTime: PropTypes.string,
      startTimeWindowDisplay: PropTypes.string,
      endTimeWindowDisplay: PropTypes.string,
    }),
  ),
  height: PropTypes.string,
};

RouteStopsMap.defaultProps = {
  stops: [],
  height: theme.sizes.routeCardMapHeight,
};

export default RouteStopsMap;
